/**
 * PowerBI Service
 * Servicio para conectarse al endpoint de PowerBI embed
 * Obtiene reportes disponibles y tokens de embed para el usuario
 */

// Endpoints de Power BI (embed token, lista de reportes, bookmarks) viven en
// nuestro backend principal (ECS). El logging a powerbi_access_log se hace ahí
// con dedup baked-in para no inflar conteos por re-fetches del token.
const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';
const POWERBI_API_URL = `${STRANDS_API_URL}/api`;

/**
 * Obtiene la lista de reportes disponibles para un usuario
 * @param {string} username - Nombre de usuario
 * @returns {Promise<Object>} - { reports, organization, role, features }
 */
export async function getAvailableReports(username) {
  try {
    const response = await fetch(
      `${POWERBI_API_URL}/powerbi-embed?username=${encodeURIComponent(username)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Error al obtener reportes: ${response.status}`);
    }

    const data = await response.json();

    return {
      reports: data.reports || [],
      organization: data.organization || null,
      role: data.role || null,
      features: data.features || {}
    };
  } catch (error) {
    console.error('[PowerBI Service] Error getting available reports:', error);
    throw error;
  }
}

/**
 * Obtiene el token de embed para un reporte específico
 * @param {string} username - Nombre de usuario
 * @param {string} reportId - ID del reporte
 * @param {string} reportName - Nombre del reporte (para logging)
 * @param {number} retryCount - Número de reintentos actuales
 * @returns {Promise<Object>} - { embedUrl, accessToken, reportId, menu, lastRefreshDate, ... }
 */
export async function getEmbedToken(username, reportId, reportName = null, retryCount = 0) {
  const MAX_RETRIES = 3;

  try {
    const response = await fetch(
      `${POWERBI_API_URL}/powerbi-embed?username=${encodeURIComponent(username)}&reportId=${encodeURIComponent(reportId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();

      // Detectar si es error de límite de workers y es retryable
      const isWorkerLimitError =
        errorData.code === 'PowerBISqlWorkersLimitReachedError' ||
        errorData.details?.retryable === true ||
        errorData.message?.includes('ocupado') ||
        errorData.message?.includes('busy');

      if (isWorkerLimitError && retryCount < MAX_RETRIES) {
        // Auto-retry con backoff exponencial
        const waitTime = Math.pow(2, retryCount + 1) * 1000; // 2s, 4s, 8s
        console.log(`[PowerBI Service] Servidor ocupado. Reintentando en ${waitTime/1000}s... (${retryCount + 1}/${MAX_RETRIES})`);

        await new Promise(resolve => setTimeout(resolve, waitTime));
        return getEmbedToken(username, reportId, reportName, retryCount + 1);
      }

      throw new Error(errorData.message || `Error al obtener token: ${response.status}`);
    }

    const data = await response.json();

    return {
      reportId: data.reportId,
      embedUrl: data.embedUrl,
      accessToken: data.accessToken,
      menu: data.menu || [],
      lastRefreshDate: data.lastRefreshDate || null,
      organization: data.organization || null,
      role: data.role || null,
      features: data.features || {},
      fromCache: data.fromCache || false
    };
  } catch (error) {
    console.error('[PowerBI Service] Error getting embed token:', error);
    throw error;
  }
}

/**
 * Obtiene los bookmarks guardados de un usuario para un reporte
 * @param {string} username - Nombre de usuario
 * @param {string} reportId - ID del reporte
 * @returns {Promise<Array>} - Lista de bookmarks
 */
export async function getBookmarks(username, reportId) {
  try {
    const response = await fetch(
      `${POWERBI_API_URL}/bookmarks?username=${encodeURIComponent(username)}&reportId=${encodeURIComponent(reportId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al obtener bookmarks');
    }

    const data = await response.json();
    return data.bookmarks || [];
  } catch (error) {
    console.error('[PowerBI Service] Error getting bookmarks:', error);
    throw error;
  }
}

/**
 * Guarda un bookmark para un usuario
 * @param {string} username - Nombre de usuario
 * @param {string} reportId - ID del reporte
 * @param {string} bookmarkName - Nombre del bookmark
 * @param {string} bookmarkState - Estado del bookmark (Base64)
 * @param {string} description - Descripción opcional
 * @returns {Promise<Object>} - Bookmark guardado
 */
export async function saveBookmark(username, reportId, bookmarkName, bookmarkState, description = '') {
  try {
    const response = await fetch(`${POWERBI_API_URL}/bookmarks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        reportId,
        bookmarkName,
        bookmarkState,
        description
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Error al guardar bookmark');
    }

    return await response.json();
  } catch (error) {
    console.error('[PowerBI Service] Error saving bookmark:', error);
    throw error;
  }
}

/**
 * Elimina un bookmark
 * @param {string} bookmarkId - ID del bookmark a eliminar
 * @param {string} username - Nombre de usuario (para verificación de ownership)
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export async function deleteBookmark(bookmarkId, username) {
  try {
    const response = await fetch(
      `${POWERBI_API_URL}/bookmarks?id=${bookmarkId}&username=${encodeURIComponent(username)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || errorData.message || 'Error al eliminar bookmark');
    }

    return true;
  } catch (error) {
    console.error('[PowerBI Service] Error deleting bookmark:', error);
    throw error;
  }
}

export default {
  getAvailableReports,
  getEmbedToken,
  getBookmarks,
  saveBookmark,
  deleteBookmark
};
